import { BANK_CLEARING_STATUS } from "../../convex/constants/bankClearing";
import { INTERNAL_ONLY_STAGES } from "../../convex/constants/orderStages";
import { ORDER_STAGE, type OrderStage } from "../../convex/constants/statuses";
import { isOnCreditTerms } from "./credit-terms";

interface BankClearingFields {
  status: string;
  bankName?: string;
  last4?: string;
}

interface PaymentWithBankClearing {
  bankClearing?: BankClearingFields;
}

export function isBankPaymentClearing(payment: PaymentWithBankClearing | undefined): boolean {
  return payment?.bankClearing?.status === BANK_CLEARING_STATUS.CLEARING;
}

export function isBankPaymentFailed(payment: PaymentWithBankClearing | undefined): boolean {
  return payment?.bankClearing?.status === BANK_CLEARING_STATUS.FAILED;
}

export function isHeldForBankPayment(payment: PaymentWithBankClearing | undefined): boolean {
  return isBankPaymentClearing(payment) || isBankPaymentFailed(payment);
}

interface StagedOrderWithBankClearing {
  stage: OrderStage;
  payment?: PaymentWithBankClearing & Parameters<typeof isOnCreditTerms>[0];
}

export function bankPaymentHoldsStage(order: StagedOrderWithBankClearing): boolean {
  if (!isHeldForBankPayment(order.payment)) return false;
  return INTERNAL_ONLY_STAGES.has(order.stage) || !isOnCreditTerms(order.payment);
}

const UNWINDING_STAGES: ReadonlySet<OrderStage> = new Set([
  ORDER_STAGE.CANCELLED,
  ORDER_STAGE.REFUNDED,
]);

function releasesForDispatch(from: OrderStage, to: OrderStage): boolean {
  if (INTERNAL_ONLY_STAGES.has(to) || UNWINDING_STAGES.has(to)) return false;
  return INTERNAL_ONLY_STAGES.has(from) || from === ORDER_STAGE.CANCELLED;
}

export function bankPaymentBlocksMove(
  order: StagedOrderWithBankClearing,
  to: OrderStage,
): boolean {
  return releasesForDispatch(order.stage, to) && bankPaymentHoldsStage(order);
}

export function bankAccountLabel(account: Pick<BankClearingFields, "bankName" | "last4">): string {
  const last4 = account.last4 ? `••${account.last4}` : undefined;
  const label = [account.bankName, last4].filter(Boolean).join(" ");
  return label || "Bank account";
}
